import React, { useState, useRef, useEffect } from 'react';
import { Tag, X, Plus, ChevronDown } from 'lucide-react';

/**
 * TagSelector Component
 * Multi-select dropdown for tags with ability to create new tags
 *
 * @param {Object} props
 * @param {Array<string>} props.selectedTags - Currently selected tag names
 * @param {Array<{name: string, count?: number}>} props.availableTags - Available tags to select from
 * @param {function} props.onTagsChange - Callback when selected tags change
 * @param {function} [props.onCreateTag] - Callback to create a new tag
 * @param {boolean} [props.allowCreate=true] - Whether to allow creating new tags
 * @param {number} [props.maxTags=5] - Maximum number of tags allowed
 * @param {string} [props.placeholder] - Placeholder text
 */
const TagSelector = ({
  selectedTags = [],
  availableTags = [],
  onTagsChange,
  onCreateTag,
  allowCreate = true,
  maxTags = 5,
  placeholder = 'Add tags...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter available tags based on search
  const filteredTags = availableTags.filter(tag =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    !selectedTags.includes(tag.name)
  );

  // Check if the search query is a new tag
  const isNewTag = searchQuery.trim() !== '' &&
    !availableTags.some(t => t.name.toLowerCase() === searchQuery.toLowerCase().trim());

  const handleToggleTag = (tagName) => {
    if (selectedTags.includes(tagName)) {
      onTagsChange(selectedTags.filter(t => t !== tagName));
    } else if (selectedTags.length < maxTags) {
      onTagsChange([...selectedTags, tagName]);
    }
  };

  const handleRemoveTag = (tagName, e) => {
    e.stopPropagation();
    onTagsChange(selectedTags.filter(t => t !== tagName));
  };

  const handleCreateTag = async () => {
    if (!onCreateTag || !searchQuery.trim() || isCreating) return;

    const newTagName = searchQuery.toLowerCase().trim();

    if (selectedTags.length >= maxTags) return;

    setIsCreating(true);
    try {
      await onCreateTag(newTagName);
      onTagsChange([...selectedTags, newTagName]);
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to create tag:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && isNewTag && allowCreate) {
      e.preventDefault();
      handleCreateTag();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Selected tags display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-1)',
          padding: 'var(--space-2)',
          minHeight: '40px',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-bg-secondary)',
          cursor: 'pointer',
          alignItems: 'center'
        }}
      >
        {selectedTags.length === 0 ? (
          <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>
            {placeholder}
          </span>
        ) : (
          selectedTags.map(tag => (
            <span
              key={tag}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: 'var(--space-1) var(--space-2)',
                background: 'var(--color-primary)',
                color: 'var(--color-primary-foreground)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 500
              }}
            >
              <Tag size={12} />
              {tag}
              <button
                onClick={(e) => handleRemoveTag(tag, e)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'inherit',
                  opacity: 0.7
                }}
              >
                <X size={12} />
              </button>
            </span>
          ))
        )}
        <ChevronDown
          size={16}
          style={{
            marginLeft: 'auto',
            color: 'var(--color-text-tertiary)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform var(--transition-fast)'
          }}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 'var(--space-1)',
            background: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 100,
            maxHeight: '250px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Search input */}
          <div style={{ padding: 'var(--space-2)', borderBottom: '1px solid var(--color-border-light)' }}>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search or create tag..."
              autoFocus
              style={{
                width: '100%',
                padding: 'var(--space-2)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-bg-secondary)',
                color: 'var(--color-text-primary)',
                fontSize: 'var(--font-size-sm)',
                outline: 'none'
              }}
            />
          </div>

          {/* Options list */}
          <div style={{ overflowY: 'auto', maxHeight: '180px' }}>
            {filteredTags.map(tag => (
              <div
                key={tag.name}
                onClick={() => handleToggleTag(tag.name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-2) var(--space-3)',
                  cursor: selectedTags.length >= maxTags ? 'not-allowed' : 'pointer',
                  opacity: selectedTags.length >= maxTags ? 0.5 : 1,
                  background: 'transparent',
                  transition: 'background var(--transition-fast)'
                }}
                onMouseEnter={(e) => {
                  if (selectedTags.length < maxTags) {
                    e.currentTarget.style.background = 'var(--color-bg-tertiary)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <Tag size={14} color="var(--color-text-tertiary)" />
                  <span style={{ fontSize: 'var(--font-size-sm)' }}>{tag.name}</span>
                </span>
                {tag.count !== undefined && (
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                    {tag.count}
                  </span>
                )}
              </div>
            ))}

            {/* Create new tag option */}
            {isNewTag && allowCreate && selectedTags.length < maxTags && (
              <div
                onClick={handleCreateTag}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) var(--space-3)',
                  cursor: isCreating ? 'wait' : 'pointer',
                  background: 'var(--color-bg-tertiary)',
                  borderTop: '1px solid var(--color-border-light)'
                }}
              >
                <Plus size={14} color="var(--color-primary)" />
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)' }}>
                  {isCreating ? 'Creating...' : `Create "${searchQuery.trim()}"`}
                </span>
              </div>
            )}

            {/* Empty state */}
            {filteredTags.length === 0 && !isNewTag && (
              <div style={{
                padding: 'var(--space-4)',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)',
                fontSize: 'var(--font-size-sm)'
              }}>
                No tags found
              </div>
            )}

            {/* Max tags reached message */}
            {selectedTags.length >= maxTags && (
              <div style={{
                padding: 'var(--space-2) var(--space-3)',
                textAlign: 'center',
                color: 'var(--color-text-tertiary)',
                fontSize: 'var(--font-size-xs)',
                background: 'var(--color-bg-tertiary)'
              }}>
                Maximum {maxTags} tags reached
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagSelector;
